export interface GalleryInfoCopy {
  intro: string;
  paragraphs: string[];
}

interface NamedCategory {
  name?: string;
  slug?: string;
}

const EXCERPT_MATCH_MIN = 40;

function slugify(value: string): string {
  return (value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function looseSlug(value: string): string {
  const slug = slugify(value);
  return slug.endsWith('s') ? slug.slice(0, -1) : slug;
}

export function normalizeCopy(value: string): string {
  return (value || '').replace(/\s+/g, ' ').trim();
}

export function splitParagraphs(value: string): string[] {
  return (value || '')
    .replace(/\r\n/g, '\n')
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function normalizeWithMap(source: string): { text: string; map: number[] } {
  let text = '';
  const map: number[] = [];
  let index = 0;
  let pendingSpaceAt = -1;
  let started = false;

  while (index < source.length) {
    if (/\s/.test(source[index])) {
      if (started && pendingSpaceAt === -1) pendingSpaceAt = index;
      index += 1;
      continue;
    }
    if (pendingSpaceAt !== -1) {
      text += ' ';
      map.push(pendingSpaceAt);
      pendingSpaceAt = -1;
    }
    started = true;
    text += source[index];
    map.push(index);
    index += 1;
  }

  return { text, map };
}

function isWordChar(value: string): boolean {
  return /[A-Za-z0-9]/.test(value);
}

function joinAroundCut(beforeRaw: string, afterRaw: string): string {
  const beforeHadBreak = /\n\s*$/.test(beforeRaw);
  const afterHadBreak = /^\s*\n/.test(afterRaw);
  const beforeText = beforeRaw.replace(/\s+$/g, '');
  const afterText = afterRaw.replace(/^\s+/g, '');
  if (!beforeText) return afterText;
  if (!afterText) return beforeText;
  if (beforeHadBreak || afterHadBreak) return `${beforeText}\n\n${afterText}`;
  return `${beforeText} ${afterText}`;
}

/**
 * Remove one bounded copy of the excerpt from display text.
 * A match that begins or ends inside a word is left untouched.
 */
function exciseExcerpt(source: string, excerptNorm: string): { text: string; excised: boolean; unbounded: boolean } {
  const { text: normalized, map } = normalizeWithMap(source);
  let from = 0;
  let unbounded = false;

  while (from < normalized.length) {
    const index = normalized.indexOf(excerptNorm, from);
    if (index < 0) break;
    const before = index === 0 ? '' : normalized[index - 1];
    const after = normalized[index + excerptNorm.length] || '';
    const bounded = (index === 0 || !isWordChar(before)) && (after === '' || !isWordChar(after));
    if (!bounded) {
      unbounded = true;
      from = index + excerptNorm.length;
      continue;
    }

    const start = map[index];
    let end = map[index + excerptNorm.length - 1] + 1;
    if (!/[.,;:!?]$/.test(excerptNorm)) {
      const trailingPunctuation = source.slice(end).match(/^[.,;:!?]+/);
      if (trailingPunctuation) end += trailingPunctuation[0].length;
    }
    return { text: joinAroundCut(source.slice(0, start), source.slice(end)), excised: true, unbounded: false };
  }

  return { text: source, excised: false, unbounded };
}

export function galleryInfoCopy(excerpt?: string, description?: string): GalleryInfoCopy {
  const excerptText = (excerpt || '').trim();
  const descriptionText = (description || '').trim();
  const excerptNorm = normalizeCopy(excerptText);

  if (!descriptionText) return { intro: excerptText, paragraphs: [] };
  if (!excerptNorm) return { intro: '', paragraphs: splitParagraphs(descriptionText) };
  if (normalizeCopy(descriptionText) === excerptNorm) return { intro: excerptText, paragraphs: [] };

  let displayText = descriptionText;
  let hideIntro = false;

  if (excerptNorm.length >= EXCERPT_MATCH_MIN && normalizeCopy(descriptionText).includes(excerptNorm)) {
    const excised = exciseExcerpt(descriptionText, excerptNorm);
    if (excised.excised) {
      displayText = excised.text;
    } else if (excised.unbounded) {
      hideIntro = true;
    }
  }

  const paragraphs = splitParagraphs(displayText).filter((paragraph) => normalizeCopy(paragraph) !== excerptNorm);
  return { intro: hideIntro ? '' : excerptText, paragraphs };
}

export function findCategory<T extends NamedCategory>(stored: string | undefined, categories: T[] = []): T | undefined {
  const value = (stored || '').trim();
  if (!value) return undefined;

  const wanted = slugify(value);
  const exact = categories.find((category) => {
    const name = (category.name || '').trim();
    const slug = slugify(category.slug || '');
    return (slug && slug === wanted) || name.toLowerCase() === value.toLowerCase() || slugify(name) === wanted;
  });
  if (exact) return exact;

  const looseWanted = looseSlug(value);
  return categories.find((category) => {
    const name = (category.name || '').trim();
    return looseSlug(category.slug || '') === looseWanted || looseSlug(name) === looseWanted;
  });
}

export function categoryDisplayName(stored?: string, categories: NamedCategory[] = []): string {
  const value = (stored || '').trim();
  if (!value) return '';

  const match = findCategory(stored, categories);
  if (match?.name?.trim()) return match.name.trim();

  if (!value.includes('-') && !value.includes('_')) return value;
  const sentence = value.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}
